import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bug,
  CheckCheck,
  CheckCircle2,
  ClipboardList,
  FolderKanban,
  Link2,
  Loader2,
  Lock,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  Save,
  Send,
  Sparkles,
  Trash2,
  Unlink,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { suggestLinks } from "@/lib/workItemMatch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AutosizeTextarea } from "@/components/ui/autosize-textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { workItemService } from "@/services/workItemService";
import type {
  WorkItem,
  WorkItemDayView,
  WorkItemLinkCandidate,
  WorkItemSourceType,
} from "@/types/worklog";

/**
 * 일일보고 업무 항목 편집기 (wlitem01)
 * - 한 번 등록한 항목은 완료할 때까지 매일 자동으로 이어진다(재입력 없음)
 * - 체크 = 그날 실제로 작업함 → 누적 작업일수
 * - 완료 처리 = 시작~완료 기간과 작업일수가 기록된다
 * 모든 변경은 즉시 저장되며 서버가 그날의 work_logs.content(필수 등록·주간보고 연동)를 함께 갱신한다.
 */

const SOURCE_ICON: Record<WorkItemSourceType, React.ElementType> = {
  issue: Bug,
  task: FolderKanban,
  task_order: Send,
};
const SOURCE_LABEL: Record<WorkItemSourceType, string> = {
  issue: "이슈",
  task: "칸반",
  task_order: "업무하달",
};

const md = (v: string | null | undefined) => {
  if (!v) return "";
  const [, m, d] = v.split("-");
  return `${Number(m)}/${Number(d)}`;
};

export interface WorkItemEditorProps {
  date: string; // YYYY-MM-DD
  isToday: boolean;
  onChanged?: () => void; // 저장 후 상위 목록/배지 갱신용
  /** 다이얼로그 등 좁은 곳: 설명 줄 생략, 카드 테두리 없이 */
  compact?: boolean;
}

export function WorkItemEditor({ date, isToday, onChanged, compact }: WorkItemEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["work-item-day", date], [date]);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => workItemService.getDay(date),
  });

  // 공통 뮤테이션 래퍼: 응답(그날 화면 전체)을 캐시에 바로 반영
  const applyView = (view: WorkItemDayView) => {
    queryClient.setQueryData(queryKey, view);
    queryClient.invalidateQueries({ queryKey: ["worklogs"] });
    queryClient.invalidateQueries({ queryKey: ["worklog", date] });
    queryClient.invalidateQueries({ queryKey: ["daily-report-status"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    onChanged?.();
  };
  const onError = (e: unknown) => {
    const err = e as { response?: { data?: { detail?: string; error?: string } } };
    toast({
      title: err?.response?.data?.detail || err?.response?.data?.error || "저장에 실패했습니다.",
      variant: "destructive",
    });
  };

  // ---- 새 항목 입력 ----
  const [newTitle, setNewTitle] = useState("");
  const [pendingLink, setPendingLink] = useState<WorkItemLinkCandidate | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 연결 제안용 후보(내 미완료 이슈·칸반·하달). 입력 중·등록 후 유사 제목을 제안한다
  const { data: candidates = [] } = useQuery({
    queryKey: ["work-item-link-candidates"],
    queryFn: () => workItemService.linkCandidates(""),
    staleTime: 60_000,
  });
  const typingSuggestions = useMemo(
    () => (pendingLink ? [] : suggestLinks(newTitle, candidates, 3)),
    [newTitle, candidates, pendingLink]
  );
  // 등록 후 제안을 "무시"한 항목 (세션 내)
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());
  // 완료 시 연결 출처 함께 처리 확인
  const [completeTarget, setCompleteTarget] = useState<WorkItem | null>(null);
  // 여러 줄 붙여넣기 → 항목 분리 확인
  const [bulkLines, setBulkLines] = useState<string[] | null>(null);
  const bulkMut = useMutation({
    mutationFn: (lines: string[]) => workItemService.createBulk(date, lines),
    onSuccess: (view, lines) => {
      applyView(view);
      setNewTitle("");
      setBulkLines(null);
      toast({ title: `${lines.length}개 항목을 등록했습니다.` });
    },
    onError,
  });
  const workedAllMut = useMutation({
    mutationFn: () => workItemService.workedAll(date),
    onSuccess: (view) => {
      applyView(view);
      toast({ title: `진행 중인 업무를 모두 ${isToday ? "오늘" : "이날"} 작업으로 체크했습니다.` });
    },
    onError,
  });
  const toPlannedMut = useMutation({
    mutationFn: (id: number) => workItemService.toPlanned(id, date),
    onSuccess: (view) => {
      applyView(view);
      toast({ title: "예정으로 되돌렸습니다. 다시 시작하면 진행 중으로 옮겨집니다." });
    },
    onError,
  });
  const dayNoteMut = useMutation({
    mutationFn: (p: { id: number; note: string }) => workItemService.setDayNote(p.id, date, p.note),
    onSuccess: applyView,
    onError,
  });
  const splitLines = (text: string) =>
    text
      .split(/\r?\n/)
      .map((l) => l.replace(/^[\s\-•·*▪]+/, "").trim())
      .filter(Boolean);

  const createMut = useMutation({
    mutationFn: (status: "in_progress" | "planned") =>
      workItemService.create({
        date,
        title: newTitle.trim(),
        status,
        source_type: pendingLink?.type ?? null,
        source_id: pendingLink?.id ?? null,
      }),
    onSuccess: (view) => {
      applyView(view);
      setNewTitle("");
      setPendingLink(null);
      inputRef.current?.focus();
    },
    onError,
  });

  const workedMut = useMutation({
    mutationFn: ({ id, worked }: { id: number; worked: boolean }) =>
      workItemService.setWorked(id, date, worked),
    onSuccess: applyView,
    onError,
  });
  const completeMut = useMutation({
    mutationFn: (p: { id: number; resolveSource?: boolean }) =>
      workItemService.complete(p.id, date, p.resolveSource ?? false),
    onSuccess: (view, p) => {
      applyView(view);
      toast({ title: p.resolveSource ? "항목과 연결된 업무를 함께 완료했습니다." : "완료 처리했습니다." });
      queryClient.invalidateQueries({ queryKey: ["work-item-link-candidates"] });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { status?: number; data?: { code?: string; error?: string; detail?: string } } };
      if (err?.response?.data?.code === "SOURCE_RESOLVE_FAILED") {
        // 항목 완료는 됐고 출처 처리만 실패 → 화면 갱신 후 안내
        queryClient.invalidateQueries({ queryKey });
        toast({
          title: "항목은 완료했지만 연결된 업무 처리는 실패했습니다.",
          description: err.response.data.error || err.response.data.detail,
          variant: "destructive",
        });
        return;
      }
      onError(e);
    },
  });
  const requestComplete = (it: WorkItem) => {
    if (it.source_type && it.source_open) setCompleteTarget(it);
    else completeMut.mutate({ id: it.id });
  };
  const reopenMut = useMutation({
    mutationFn: (id: number) => workItemService.reopen(id, date),
    onSuccess: applyView,
    onError,
  });
  const removeMut = useMutation({
    mutationFn: (id: number) => workItemService.remove(id, date),
    onSuccess: (view) => {
      applyView(view);
      toast({ title: "항목을 삭제했습니다." });
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (p: { id: number; title?: string; clear_source?: boolean; link?: WorkItemLinkCandidate }) =>
      workItemService.update(p.id, {
        date,
        title: p.title,
        clear_source: p.clear_source,
        source_type: p.link?.type,
        source_id: p.link?.id,
      }),
    onSuccess: applyView,
    onError,
  });

  // ---- 특이사항 ----
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  useEffect(() => {
    setNotes(data?.worklog.notes || "");
    setNotesDirty(false);
  }, [data?.worklog.notes, date]);
  const notesMut = useMutation({
    mutationFn: () => workItemService.updateNotes(date, notes.trim()),
    onSuccess: (view) => {
      applyView(view);
      setNotesDirty(false);
      toast({ title: "특이사항을 저장했습니다." });
    },
    onError,
  });

  // ---- 분류 ----
  const items = data?.items || [];
  const locked = Boolean(data?.locked);
  const inProgress = items.filter((i) => i.status === "in_progress");
  const doneToday = items.filter((i) => i.status === "done" && i.completed_on === date);
  const planned = items.filter((i) => i.status === "planned");
  const workedToday = inProgress.filter((i) => i.worked_on_date).length;
  const uncheckedCarried = inProgress.filter((i) => !i.worked_on_date && i.started_on && i.started_on < date).length;
  const staleCount = inProgress.filter((i) => i.stale).length;
  const [notesOpen, setNotesOpen] = useState(false);

  const submitNew = (status: "in_progress" | "planned") => {
    if (!newTitle.trim()) {
      toast({ title: "업무 내용을 입력하세요.", variant: "destructive" });
      return;
    }
    createMut.mutate(status);
  };

  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="py-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={cn(compact && "border-0 shadow-none")}>
        <CardHeader className={cn("pb-3", compact && "px-0 pt-0")}>
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <ClipboardList className="h-5 w-5 text-blue-600" />
            업무 항목
            <Badge variant="secondary">진행 {inProgress.length}</Badge>
            {doneToday.length > 0 && (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                {isToday ? "오늘" : "이날"} 완료 {doneToday.length}
              </Badge>
            )}
            {planned.length > 0 && <Badge variant="outline">예정 {planned.length}</Badge>}
          </CardTitle>
          {!compact && (
            <CardDescription>
              등록한 업무는 완료할 때까지 매일 자동으로 이어집니다. 체크하면 {isToday ? "오늘" : "이날"} 작업한
              것으로 기록되고, 완료하면 시작~완료 기간과 작업일수가 남습니다.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className={cn("space-y-5", compact && "px-0 pb-0")}>
          {locked && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">이 날짜는 수정할 수 없습니다.</div>
                <div className="text-xs">{data?.lock_reason}</div>
              </div>
            </div>
          )}
          {/* 새 항목 입력 */}
          <div className={cn("space-y-2", locked && "pointer-events-none opacity-50")}>
            <div className="flex gap-2">
              <AutosizeTextarea
                ref={inputRef as unknown as React.Ref<HTMLTextAreaElement>}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value.replace(/\r?\n/g, " "))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submitNew("in_progress");
                  }
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  const lines = splitLines(text);
                  if (lines.length >= 2) {
                    e.preventDefault();
                    setBulkLines(lines);
                  }
                }}
                minRows={1}
                placeholder={`${isToday ? "오늘" : "이날"} 진행한 업무 한 줄을 입력하고 Enter (여러 줄 붙여넣기 = 줄마다 항목)`}
                className="flex-1 min-h-0 max-h-28 py-2 text-sm"
                disabled={createMut.isPending}
              />
              <LinkPicker
                selected={pendingLink}
                onSelect={(c) => {
                  setPendingLink(c);
                  if (c && !newTitle.trim()) setNewTitle(c.title);
                }}
              />
              <Button onClick={() => submitNew("in_progress")} disabled={createMut.isPending}>
                {createMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                등록
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="self-center text-muted-foreground"
                onClick={() => submitNew("planned")}
                disabled={createMut.isPending || !newTitle.trim()}
                title="아직 시작하지 않은 업무로 등록"
              >
                예정으로
              </Button>
            </div>
            {pendingLink && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Link2 className="h-3 w-3" />
                {SOURCE_LABEL[pendingLink.type]} 연결: {pendingLink.title}
                <button
                  type="button"
                  className="underline ml-1"
                  onClick={() => setPendingLink(null)}
                >
                  해제
                </button>
              </div>
            )}
            {typingSuggestions.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Sparkles className="h-3 w-3 text-amber-500" />
                  비슷한 내 업무와 연결:
                </span>
                {typingSuggestions.map((c) => {
                  const Icon = SOURCE_ICON[c.type];
                  return (
                    <button
                      key={`${c.type}-${c.id}`}
                      type="button"
                      className="inline-flex max-w-[320px] items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-sky-800 hover:bg-sky-100"
                      title={`${SOURCE_LABEL[c.type]}${c.subtitle ? ` · ${c.subtitle}` : ""} 와 연결해 등록`}
                      onClick={() => {
                        setPendingLink(c);
                        inputRef.current?.focus();
                      }}
                    >
                      <Icon className="h-3 w-3 shrink-0" />
                      <span className="truncate">{c.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* 진행 중 */}
          <Section
            title="진행 중"
            hint={
              inProgress.length > 0
                ? `${workedToday}/${inProgress.length}건 ${isToday ? "오늘" : "이날"} 작업 체크${staleCount ? ` · 정리 필요 ${staleCount}` : ""}`
                : undefined
            }
            action={
              uncheckedCarried > 0 && !locked ? (
                <Button size="sm" variant="outline" className="h-7" onClick={() => workedAllMut.mutate()} disabled={workedAllMut.isPending}>
                  <CheckCheck className="h-3.5 w-3.5 mr-1 text-blue-600" />
                  {isToday ? "오늘" : "이날"} 모두 작업 ({uncheckedCarried})
                </Button>
              ) : undefined
            }
            empty="진행 중인 업무가 없습니다. 위에 입력해 등록하세요."
          >
            {inProgress.map((it) => (
              <ItemRow
                key={it.id}
                item={it}
                date={date}
                busy={workedMut.isPending || completeMut.isPending || locked}
                locked={locked}
                suggestion={
                  !it.source_type && !dismissed.has(it.id)
                    ? suggestLinks(it.title, candidates, 1)[0]
                    : undefined
                }
                onDismissSuggestion={() => setDismissed((prev) => new Set(prev).add(it.id))}
                onSaveNote={(note) => dayNoteMut.mutate({ id: it.id, note })}
                dayWord={isToday ? "오늘" : "이날"}
                onWorked={(w) => workedMut.mutate({ id: it.id, worked: w })}
                onToPlanned={() => toPlannedMut.mutate(it.id)}
                onComplete={() => requestComplete(it)}
                onRename={(t) => updateMut.mutate({ id: it.id, title: t })}
                onLink={(c) => updateMut.mutate({ id: it.id, link: c })}
                onUnlink={() => updateMut.mutate({ id: it.id, clear_source: true })}
                onDelete={() => removeMut.mutate(it.id)}
              />
            ))}
          </Section>

          {/* 완료 */}
          {doneToday.length > 0 && (
            <Section title={`${isToday ? "오늘" : "이날"} 완료`}>
              {doneToday.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  date={date}
                  done
                  busy={reopenMut.isPending || locked}
                  locked={locked}
                  onSaveNote={(note) => dayNoteMut.mutate({ id: it.id, note })}
                  dayWord={isToday ? "오늘" : "이날"}
                  onReopen={() => reopenMut.mutate(it.id)}
                  onRename={(t) => updateMut.mutate({ id: it.id, title: t })}
                  onLink={(c) => updateMut.mutate({ id: it.id, link: c })}
                  onUnlink={() => updateMut.mutate({ id: it.id, clear_source: true })}
                  onDelete={() => removeMut.mutate(it.id)}
                />
              ))}
            </Section>
          )}

          {/* 예정 */}
          {planned.length > 0 && (
            <Section title="예정" hint="시작을 누르면 진행 중으로 옮겨집니다">
              {planned.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  date={date}
                  plannedRow
                  busy={workedMut.isPending || locked}
                  locked={locked}
                  onStart={() => workedMut.mutate({ id: it.id, worked: true })}
                  onRename={(t) => updateMut.mutate({ id: it.id, title: t })}
                  onLink={(c) => updateMut.mutate({ id: it.id, link: c })}
                  onUnlink={() => updateMut.mutate({ id: it.id, clear_source: true })}
                  onDelete={() => removeMut.mutate(it.id)}
                />
              ))}
            </Section>
          )}
        </CardContent>
      </Card>

      {/* 여러 줄 붙여넣기 → 항목 분리 */}
      <AlertDialog open={!!bulkLines} onOpenChange={(o) => !o && setBulkLines(null)}>
        <AlertDialogContent className="max-w-lg w-[calc(100vw-2rem)] overflow-hidden">
          <AlertDialogHeader className="min-w-0">
            <AlertDialogTitle>{bulkLines?.length}개 항목으로 등록할까요?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="min-w-0 space-y-2 text-sm">
                <p className="break-words">
                  붙여넣은 내용을 줄마다 하나의 업무 항목으로 등록합니다. 항목은 한 줄 제목이 기준이고, 상세 내용은 각
                  항목의 메모에 적을 수 있습니다.
                </p>
                <ul className="max-h-56 min-w-0 overflow-y-auto overflow-x-hidden rounded border bg-muted/30 p-2 text-xs">
                  {(bulkLines || []).map((l, i) => (
                    <li key={i} className="flex gap-1 whitespace-pre-wrap break-all">
                      <span className="shrink-0 text-muted-foreground">·</span>
                      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{l}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-wrap gap-2 sm:space-x-0">
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                setNewTitle((bulkLines || []).join(" / "));
                setBulkLines(null);
              }}
            >
              한 항목으로 합치기
            </AlertDialogAction>
            <AlertDialogAction onClick={() => bulkLines && bulkMut.mutate(bulkLines)}>
              {bulkLines?.length}개로 등록
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 완료 시 연결 출처 함께 처리 */}
      <AlertDialog open={!!completeTarget} onOpenChange={(o) => !o && setCompleteTarget(null)}>
        <AlertDialogContent className="max-w-lg w-[calc(100vw-2rem)] overflow-hidden">
          <AlertDialogHeader className="min-w-0">
            <AlertDialogTitle>
              연결된 {completeTarget?.source_type ? SOURCE_LABEL[completeTarget.source_type] : "업무"}도 함께 완료할까요?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="min-w-0 space-y-1 text-sm">
                <p className="break-words font-medium text-foreground">{completeTarget?.source_title || completeTarget?.title}</p>
                <p>
                  함께 완료하면 {completeTarget?.source_type === "issue" ? "이슈가 완료 처리되고 관련 알림이" : completeTarget?.source_type === "task" ? "칸반 카드가 완료로 이동하고 이력이" : "업무 하달이 완료(검토 요청) 처리되고 알림이"} 평소와 같이 발송됩니다. 두 군데를 따로 처리하지 않아도 됩니다.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                if (completeTarget) completeMut.mutate({ id: completeTarget.id });
                setCompleteTarget(null);
              }}
            >
              항목만 완료
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                if (completeTarget) completeMut.mutate({ id: completeTarget.id, resolveSource: true });
                setCompleteTarget(null);
              }}
            >
              함께 완료
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 특이사항 — 내용이 없으면 접어 둔다(거의 안 쓰는 칸) */}
      {!(notes || notesOpen || notesDirty) ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => setNotesOpen(true)}
          disabled={locked}
        >
          <AlertCircle className="h-4 w-4 text-purple-600" />
          특이사항·건의 추가
        </button>
      ) : (
      <Card className={cn(compact && "border-0 shadow-none")}>
        <CardContent className={cn("py-6 space-y-3", compact && "px-0 py-2")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-purple-600" />
              <span className="font-semibold">특이사항</span>
            </div>
            <Button
              size="sm"
              variant={notesDirty ? "default" : "outline"}
              onClick={() => notesMut.mutate()}
              disabled={!notesDirty || notesMut.isPending || locked}
            >
              <Save className="h-4 w-4 mr-1" />
              {notesMut.isPending ? "저장 중..." : "특이사항 저장"}
            </Button>
          </div>
          <Textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setNotesDirty(true);
            }}
            placeholder="특이사항이나 건의사항을 입력하세요 (주간보고서 이슈·건의에 자동 반영)"
            rows={3}
            disabled={locked}
            autoFocus={notesOpen && !notes}
          />
        </CardContent>
      </Card>
      )}
    </>
  );
}

// ---- 하위 컴포넌트 -----------------------------------------------------------

function Section({
  title,
  hint,
  empty,
  action,
  children,
}: {
  title: string;
  hint?: string;
  empty?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const count = Array.isArray(children) ? children.length : children ? 1 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-semibold">{title}</span>
        <span className="flex items-center gap-2">
          {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
          {action}
        </span>
      </div>
      {count === 0 && empty ? (
        <p className="text-sm text-muted-foreground py-2">{empty}</p>
      ) : (
        <div className="divide-y rounded-md border">{children}</div>
      )}
    </div>
  );
}

function ItemRow({
  item,
  date,
  done,
  plannedRow,
  busy,
  onWorked,
  onComplete,
  onReopen,
  onStart,
  onRename,
  onLink,
  onUnlink,
  onDelete,
  suggestion,
  onDismissSuggestion,
  onSaveNote,
  dayWord = "오늘",
  locked,
  onToPlanned,
}: {
  item: WorkItem;
  date: string;
  done?: boolean;
  plannedRow?: boolean;
  busy?: boolean;
  locked?: boolean;
  suggestion?: WorkItemLinkCandidate & { score: number };
  onDismissSuggestion?: () => void;
  onSaveNote?: (note: string) => void;
  dayWord?: string;
  onToPlanned?: () => void;
  onWorked?: (worked: boolean) => void;
  onComplete?: () => void;
  onReopen?: () => void;
  onStart?: () => void;
  onRename: (title: string) => void;
  onLink: (c: WorkItemLinkCandidate) => void;
  onUnlink: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);
  const [linkOpen, setLinkOpen] = useState(false);
  useEffect(() => setDraft(item.title), [item.title]);
  // 작업 메모(여러 줄): 메모가 있으면 펼침, 없으면 "메모" 버튼으로 연다
  const [noteOpen, setNoteOpen] = useState(Boolean(item.day_note));
  const [noteDraft, setNoteDraft] = useState(item.day_note || "");
  useEffect(() => {
    setNoteDraft(item.day_note || "");
    if (item.day_note) setNoteOpen(true);
  }, [item.day_note]);
  const commitNote = () => {
    const t = noteDraft.trim();
    if (t !== (item.day_note || "")) onSaveNote?.(t);
    if (!t) setNoteOpen(false);
  };

  const commitRename = () => {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== item.title) onRename(t);
    else setDraft(item.title);
  };

  const SourceIcon = item.source_type ? SOURCE_ICON[item.source_type] : null;
  const carriedOver = !done && !plannedRow && item.started_on && item.started_on < date;

  return (
    <div
      className={cn(
        "flex items-start gap-2 px-3 py-2",
        done && "bg-green-50/60",
        !done && !plannedRow && !item.worked_on_date && "bg-muted/30",
        item.stale && !item.worked_on_date && "opacity-75"
      )}
    >
      {/* 왼쪽 컨트롤 */}
      <div className="pt-0.5">
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : plannedRow ? (
          <span className="inline-block h-5 w-5" />
        ) : (
          <Checkbox
            checked={item.worked_on_date}
            disabled={busy}
            onCheckedChange={(v) => onWorked?.(v === true)}
            title={item.worked_on_date ? "이날 작업함 (해제하려면 클릭)" : "이날 작업했으면 체크"}
          />
        )}
      </div>

      {/* 본문 */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) commitRename();
              if (e.key === "Escape") {
                setDraft(item.title);
                setEditing(false);
              }
            }}
            className="h-8"
          />
        ) : (
          <div
            className={cn(
              "text-sm break-words cursor-text",
              done && "text-muted-foreground line-through decoration-green-600/60"
            )}
            onDoubleClick={() => !locked && setEditing(true)}
            title={locked ? "수정 잠김" : "더블클릭하면 수정"}
          >
            {item.title}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          {SourceIcon && item.source_type && (
            <span className="inline-flex items-center gap-0.5 text-sky-700">
              <SourceIcon className="h-3 w-3" />
              {SOURCE_LABEL[item.source_type]} 연결
            </span>
          )}
          {plannedRow ? (
            <span>등록 {md(item.created_on)}</span>
          ) : (
            <>
              <span>시작 {md(item.started_on)}</span>
              {done && item.completed_on && <span>완료 {md(item.completed_on)}</span>}
              <span>작업 {item.work_days}일</span>
              {item.elapsed_business_days > item.work_days && (
                <span>소요 {item.elapsed_business_days}일(평일)</span>
              )}
              {carriedOver && !item.worked_on_date && !item.stale && (
                <span className="text-amber-700">이어진 업무 · 작업했으면 체크</span>
              )}
              {item.stale && !item.worked_on_date && (
                <span className="text-rose-700">{item.stale_days}일째 작업 체크 없음 · 정리 필요</span>
              )}
              {item.auto_closed && <span className="text-slate-500">자동 종료</span>}
              {item.closed_by_source && <span className="text-sky-700">연결 업무 완료로 자동 처리</span>}
            </>
          )}
        </div>
        {suggestion && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border border-dashed border-amber-300 bg-amber-50/60 px-2 py-1 text-[11px] text-amber-900">
            <Sparkles className="h-3 w-3 shrink-0 text-amber-500" />
            <span className="shrink-0">이 {SOURCE_LABEL[suggestion.type]}와 연결할까요?</span>
            <span className="truncate font-medium" title={suggestion.title}>
              {suggestion.title}
              {suggestion.subtitle ? ` · ${suggestion.subtitle}` : ""}
            </span>
            <span className="ml-auto inline-flex shrink-0 gap-1">
              <button
                type="button"
                className="rounded border border-amber-300 bg-white px-1.5 py-0.5 hover:bg-amber-100"
                onClick={() => onLink(suggestion)}
              >
                연결
              </button>
              <button
                type="button"
                className="rounded px-1.5 py-0.5 text-amber-700 hover:bg-amber-100"
                onClick={onDismissSuggestion}
              >
                무시
              </button>
            </span>
          </div>
        )}
        {item.stale && !item.worked_on_date && !locked && !done && !plannedRow && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border border-dashed border-rose-300 bg-rose-50/60 px-2 py-1 text-[11px] text-rose-900">
            <span className="shrink-0">이 업무는 어떻게 할까요?</span>
            <span className="ml-auto inline-flex shrink-0 gap-1">
              <button type="button" className="rounded border border-rose-300 bg-white px-1.5 py-0.5 hover:bg-rose-100" onClick={onComplete}>
                완료
              </button>
              <button type="button" className="rounded border border-rose-300 bg-white px-1.5 py-0.5 hover:bg-rose-100" onClick={onToPlanned}>
                예정으로
              </button>
              <button type="button" className="rounded px-1.5 py-0.5 text-rose-700 hover:bg-rose-100" onClick={() => onWorked?.(true)}>
                아직 진행 ({dayWord} 작업 체크)
              </button>
            </span>
          </div>
        )}
        {onSaveNote && !plannedRow && !(locked && !item.day_note) && (
          noteOpen ? (
            <AutosizeTextarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onBlur={commitNote}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setNoteDraft(item.day_note || "");
                  if (!item.day_note) setNoteOpen(false);
                }
              }}
              minRows={1}
              autoFocus={!item.day_note}
              disabled={locked}
              placeholder={`${dayWord} 이 업무에서 한 일 (여러 줄 가능, 주간보고서에 날짜별로 표시)`}
              className="mt-1.5 min-h-0 max-h-48 py-1.5 text-xs"
            />
          ) : (
            <button
              type="button"
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() => setNoteOpen(true)}
            >
              <MessageSquareText className="h-3 w-3" />
              {dayWord} 작업 메모
            </button>
          )
        )}
      </div>

      {/* 오른쪽 액션 */}
      <div className="flex items-center gap-1 shrink-0">
        {!done && !plannedRow && (
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={onComplete}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-green-600" />
            완료
          </Button>
        )}
        {plannedRow && (
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={onStart}>
            <Play className="h-3.5 w-3.5 mr-1 text-blue-600" />
            시작
          </Button>
        )}
        {done && (
          <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={onReopen}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            재개
          </Button>
        )}
        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={locked}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                내용 수정
              </DropdownMenuItem>
              <PopoverTrigger asChild>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setLinkOpen(true); }}>
                  <Link2 className="h-4 w-4 mr-2" />
                  {item.source_type ? "연결 변경" : "이슈·칸반·하달 연결"}
                </DropdownMenuItem>
              </PopoverTrigger>
              {item.source_type && (
                <DropdownMenuItem onClick={onUnlink}>
                  <Unlink className="h-4 w-4 mr-2" />
                  연결 해제
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {item.deletable !== false ? (
                <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  삭제
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem disabled title="지난 날짜의 기록이 있는 항목은 삭제할 수 없습니다">
                  <Trash2 className="h-4 w-4 mr-2" />
                  삭제 불가 · 완료 처리나 체크 해제로
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <PopoverContent align="end" className="p-0 w-[360px]">
            <LinkCommand
              onSelect={(c) => {
                setLinkOpen(false);
                onLink(c);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

// 새 항목 입력줄의 연결 선택 버튼
function LinkPicker({
  selected,
  onSelect,
}: {
  selected: WorkItemLinkCandidate | null;
  onSelect: (c: WorkItemLinkCandidate | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={selected ? "secondary" : "outline"}
          size="icon"
          title="내 이슈·칸반·업무하달과 연결 (주간보고서에서 한 줄로 합쳐집니다)"
        >
          <Link2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-[360px]">
        <LinkCommand
          onSelect={(c) => {
            setOpen(false);
            onSelect(c);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function LinkCommand({ onSelect }: { onSelect: (c: WorkItemLinkCandidate) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["work-item-link-candidates"],
    queryFn: () => workItemService.linkCandidates(""),
    staleTime: 60_000,
  });
  const groups: { type: WorkItemSourceType; label: string }[] = [
    { type: "issue", label: "이슈" },
    { type: "task", label: "칸반 업무" },
    { type: "task_order", label: "업무 하달" },
  ];
  return (
    <Command>
      <CommandInput placeholder="내가 맡은 이슈·칸반·하달 검색" />
      <CommandList>
        {isLoading ? (
          <div className="p-3 text-sm text-muted-foreground">불러오는 중...</div>
        ) : (
          <>
            <CommandEmpty>연결할 항목이 없습니다.</CommandEmpty>
            {groups.map((g) => {
              const rows = (data || []).filter((c) => c.type === g.type);
              if (rows.length === 0) return null;
              return (
                <CommandGroup key={g.type} heading={g.label}>
                  {rows.map((c) => {
                    const Icon = SOURCE_ICON[c.type];
                    return (
                      <CommandItem
                        key={`${c.type}-${c.id}`}
                        value={`${c.title} ${c.subtitle || ""}`}
                        onSelect={() => onSelect(c)}
                      >
                        <Icon className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="truncate">{c.title}</span>
                        {c.subtitle && (
                          <span className="ml-auto text-xs text-muted-foreground pl-2 shrink-0">
                            {c.subtitle}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </>
        )}
      </CommandList>
    </Command>
  );
}

export default WorkItemEditor;
